
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, AlertTriangle, Info, CreditCard } from 'lucide-react';

export const PaymentTestGuide = () => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Complete Payment Flow Testing Guide
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Pre-Testing Checklist */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Pre-Testing Checklist
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="auth" />
                <label htmlFor="auth">User authentication working</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="stripe" />
                <label htmlFor="stripe">Stripe secret key configured</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="functions" />
                <label htmlFor="functions">Edge functions deployed</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="db" />
                <label htmlFor="db">Database tables exist (subscribers, etc.)</label>
              </div>
            </div>
          </div>

          <Separator />

          {/* Test Scenarios */}
          <div>
            <h3 className="font-semibold mb-3">Test Scenarios to Cover</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              <Card className="p-4">
                <h4 className="font-medium text-green-700 mb-2">✅ Happy Path Tests</h4>
                <ul className="text-sm space-y-1">
                  <li>• Successful Starter subscription</li>
                  <li>• Successful Premium subscription</li>
                  <li>• Successful Enterprise contact</li>
                  <li>• Payment success page redirect</li>
                  <li>• Subscription status update</li>
                  <li>• Customer portal access</li>
                </ul>
              </Card>

              <Card className="p-4">
                <h4 className="font-medium text-red-700 mb-2">❌ Error Path Tests</h4>
                <ul className="text-sm space-y-1">
                  <li>• Payment declined (test card)</li>
                  <li>• Payment cancelled by user</li>
                  <li>• Network timeout during payment</li>
                  <li>• Unauthenticated user attempt</li>
                  <li>• Invalid plan selection</li>
                  <li>• Stripe service unavailable</li>
                </ul>
              </Card>
            </div>
          </div>

          <Separator />

          {/* Stripe Test Cards */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Stripe Test Cards
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <h4 className="font-medium text-green-800 mb-2">Successful Payments</h4>
                <div className="space-y-1 font-mono text-xs">
                  <div>4242 4242 4242 4242 (Visa)</div>
                  <div>5555 5555 5555 4444 (Mastercard)</div>
                  <div>3782 822463 10005 (Amex)</div>
                </div>
              </div>
              
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <h4 className="font-medium text-red-800 mb-2">Failed Payments</h4>
                <div className="space-y-1 font-mono text-xs">
                  <div>4000 0000 0000 0002 (Declined)</div>
                  <div>4000 0000 0000 9995 (Insufficient funds)</div>
                  <div>4000 0000 0000 0069 (Expired card)</div>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Use any future expiry date, any 3-digit CVC, and any postal code
            </p>
          </div>

          <Separator />

          {/* Step-by-Step Testing */}
          <div>
            <h3 className="font-semibold mb-3">Step-by-Step Testing Process</h3>
            <div className="space-y-4">
              
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">Step 1</Badge>
                  <span className="font-medium">Frontend Button Test</span>
                </div>
                <ul className="text-sm space-y-1 ml-4">
                  <li>• Click "Start Free Trial" button</li>
                  <li>• Verify authentication check works</li>
                  <li>• Check console for any JavaScript errors</li>
                  <li>• Confirm loading states display correctly</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">Step 2</Badge>
                  <span className="font-medium">Edge Function Call</span>
                </div>
                <ul className="text-sm space-y-1 ml-4">
                  <li>• Monitor network tab for create-checkout call</li>
                  <li>• Check edge function logs for processing steps</li>
                  <li>• Verify proper plan parameter passed</li>
                  <li>• Confirm checkout URL returned</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">Step 3</Badge>
                  <span className="font-medium">Stripe Checkout</span>
                </div>
                <ul className="text-sm space-y-1 ml-4">
                  <li>• Verify redirect to Stripe Checkout</li>
                  <li>• Check correct plan and pricing displayed</li>
                  <li>• Test with successful test card</li>
                  <li>• Test with declined test card</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">Step 4</Badge>
                  <span className="font-medium">Payment Completion</span>
                </div>
                <ul className="text-sm space-y-1 ml-4">
                  <li>• Verify redirect to success page</li>
                  <li>• Check subscription status updates</li>
                  <li>• Test verify-payment function if implemented</li>
                  <li>• Confirm database records updated</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">Step 5</Badge>
                  <span className="font-medium">UI State Refresh</span>
                </div>
                <ul className="text-sm space-y-1 ml-4">
                  <li>• Verify subscription context updates</li>
                  <li>• Check pricing page shows current plan</li>
                  <li>• Test customer portal access</li>
                  <li>• Confirm feature access based on plan</li>
                </ul>
              </div>
            </div>
          </div>

          <Separator />

          {/* Environment Specific Testing */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              Environment-Specific Considerations
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-2">Development Mode</h4>
                <ul className="text-sm space-y-1">
                  <li>• Use Stripe test keys</li>
                  <li>• Test cards work normally</li>
                  <li>• Edge functions run locally</li>
                  <li>• Database is development instance</li>
                </ul>
              </div>
              
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-2">Production Mode</h4>
                <ul className="text-sm space-y-1">
                  <li>• Use Stripe live keys</li>
                  <li>• Real payment processing</li>
                  <li>• Edge functions deployed</li>
                  <li>• Production database</li>
                </ul>
              </div>
            </div>
          </div>

          <Separator />

          {/* Common Issues */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Info className="h-4 w-4 text-blue-500" />
              Common Issues & Solutions
            </h3>
            
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <h4 className="font-medium text-blue-800">Issue: Payment succeeds but subscription not updated</h4>
                <p className="text-blue-700 mt-1">Solution: Check verify-payment function and subscription context refresh</p>
              </div>
              
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <h4 className="font-medium text-blue-800">Issue: Checkout session creation fails</h4>
                <p className="text-blue-700 mt-1">Solution: Verify Stripe secret key and edge function logs</p>
              </div>
              
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <h4 className="font-medium text-blue-800">Issue: User not redirected after payment</h4>
                <p className="text-blue-700 mt-1">Solution: Check success_url configuration in create-checkout</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
